import type { RefObject } from 'react';
import {
  Caption1,
  Checkbox,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tag,
  Text,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { packetGroupLabel } from '@vigeo/shared';
import { PopupDialog, tokens } from '@vigeo/ui';
import {
  confirmSendCopy,
  emailChoiceHint,
  emailChoiceLabel,
  packetFootnote,
  packetIntro,
  portalLinkNote,
} from './model.js';
import type { OnboardingPreview } from '@vigeo/shared';

/**
 * 2.3 — the confirm window behind **Send Onboarding Items** (Paul, 2026-09-29 15:45; #373).
 *
 * Everything here USED TO BE inline on 2.2. It is moved, not rewritten: the same `packetIntro`,
 * `packetFootnote`, `portalLinkNote` and email choice. A second wording of the same facts is how two
 * screens start disagreeing about how many forms a candidate is about to receive.
 *
 * The email checkbox lives HERE rather than on 2.2 because this is the last stop before the
 * candidate is written to, and the two decisions — send the forms, write to the person — are taken
 * in the same breath. `OfferStageSection` still performs them as two separate API calls for the
 * reasons its own comment gives; this is one confirmation of two things, not one call.
 */

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  tags: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  muted: { color: tokens.colorNeutralForeground2 },
  /*
    The same tile as 2.2 used, copied rather than re-derived — including `border` as the FULL
    shorthand. Griffel rejects the partial ones (`borderWidth`, `borderColor`) because it cannot
    reason about which longhand a later rule overrides, and the error only surfaces in the web build.
  */
  choice: {
    display: 'flex', flexDirection: 'row', alignItems: 'center',
    paddingInline: tokens.spacingHorizontalM,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    minHeight: '2.75rem',
  },
  choiceOn: { backgroundColor: tokens.colorBrandBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}` },
});

export interface ConfirmSendOnboardingProps {
  readonly open: boolean;
  readonly firstName: string;
  readonly roleLabel: string | null;
  readonly candidateEmail: string;
  /** The packet, as 2.2 already fetched it. Undefined while loading, null when it could not load. */
  readonly preview: OnboardingPreview | null | undefined;
  readonly email: boolean;
  readonly onEmailChange: (email: boolean) => void;
  readonly busy: boolean;
  /**
   * False when the send would be refused whatever this window shows — today, a blank audit reason,
   * which lives on 2.2. The gate is duplicated rather than moved: 2.2's stops a pointless trip into
   * a window that cannot send, and this one guards the action itself.
   */
  readonly canSend: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  /** The button that opened it, so the keyboard goes back there (Safari on a Mac does not focus a button on click). */
  readonly opener?: RefObject<HTMLElement | null>;
}

export function ConfirmSendOnboarding({
  open,
  firstName,
  roleLabel,
  candidateEmail,
  preview,
  email,
  onEmailChange,
  busy,
  canSend,
  onCancel,
  onConfirm,
  opener,
}: ConfirmSendOnboardingProps) {
  const styles = useStyles();

  return (
    <PopupDialog
      open={open}
      title={confirmSendCopy.title}
      size="medium"
      // It writes to the candidate and cannot be taken back with one click (PG8), so the keyboard starts on Cancel.
      startOn="cancel"
      opener={opener}
      busy={busy}
      onClose={onCancel}
      /*
        NAMED APART FROM 2.2's Cancel, which is still in the tree behind this window and means
        something else entirely — it dismisses the warning banner. Two buttons with one
        accessible name and two meanings is the "Change stage" collision again, and it also
        makes `getByRole('button', { name: 'Cancel' })` throw on multiple matches.

        The visible text stays Paul's; the accessible name only says which Cancel this is.
      */
      cancelLabel={confirmSendCopy.cancel}
      cancelAriaLabel={`${confirmSendCopy.cancel}: ${confirmSendCopy.title}`}
      primary={{ label: confirmSendCopy.confirm, onClick: onConfirm, disabled: !canSend }}
      // The email choice is the last decision before the candidate is written to: it stays above the buttons while the list scrolls (card 15).
      pinned={
        <div className={styles.body}>
          <div className={mergeClasses(styles.choice, email && styles.choiceOn)}>
            <Checkbox
              checked={email}
              disabled={busy}
              onChange={(_, d) => onEmailChange(d.checked === true)}
              label={emailChoiceLabel(firstName)}
            />
          </div>
          <Caption1 className={styles.muted}>{emailChoiceHint(candidateEmail)}</Caption1>
        </div>
      }
    >
      <div className={styles.body}>
        <Text>{confirmSendCopy.lede(firstName)}</Text>

        {preview === undefined ? (
          <Spinner size="tiny" label="Working out the onboarding steps" />
        ) : preview === null ? (
          /*
           * The packet could not be worked out, and the send still works — the steps are created
           * either way. Saying so matters: without it this reads as "nothing will be sent", and
           * somebody cancels a send that would have been correct.
           */
          <MessageBar intent="warning">
            <MessageBarBody>
              The list of onboarding steps could not be worked out. Sending still works; the steps
              are created either way.
            </MessageBarBody>
          </MessageBar>
        ) : (
          <>
            <Text>{packetIntro(preview.total, roleLabel)}</Text>
            <div className={styles.tags}>
              {preview.groups.map((group) => (
                <Tag key={group.type} appearance="outline" size="small">
                  {packetGroupLabel(group.type, group.count)}
                </Tag>
              ))}
            </div>
            <Caption1 className={styles.muted}>{packetFootnote}</Caption1>
            {preview.gaps.length === 0 ? null : (
              <MessageBar intent="warning">
                <MessageBarBody>
                  {preview.gaps.length} requirement(s) apply to {firstName} that no published form
                  can close, so they will not be in the packet.
                </MessageBarBody>
              </MessageBar>
            )}
          </>
        )}

        {/* Paul names 3.6 explicitly. "21 forms" invites "is that all of them"; this answers it. */}
        <Caption1 className={styles.muted}>{confirmSendCopy.laterAtOrientation}</Caption1>

        {/* The link the candidate already holds does not change — the one thing HR asks about most. */}
        <Text>{portalLinkNote(null, null)}</Text>
      </div>
    </PopupDialog>
  );
}
